import axios, { AxiosInstance } from 'axios';
import { cloneDeep } from 'lodash';

import { billingErrorHandler } from './billing_error_handler';
import { refreshTokenMiddleware, tokenErrorHandler, tokenStructure, refreshForRunner } from './refresh_token';
import { verifyTokenApi } from './verify_api'; // imported only to refresh the tokens in the same place, use this api only for verify api
import ErrorHandlingService, { ERROR_CODES, STATUS_CODES } from '@/utils/error-handling-service';

/* eslint-disable no-undef */
const { VUE_APP_API, VUE_APP_APIGW } = process.env;

if (!VUE_APP_API || !VUE_APP_APIGW) {
	throw new Error('Missing api URLs');
}

const createApi = () => {
	const api = axios.create({
		baseURL: VUE_APP_API,
		headers: cloneDeep(axios.defaults.headers),
		withCredentials: true,
	});
	api.interceptors.request.use(
		async (config) => await refreshTokenMiddleware(config),
		(err) => {
			try {
				tokenErrorHandler({ err });
			} catch {
				billingErrorHandler({ err });
			}
		}
	);

	api.interceptors.response.use(
		(response) => response,
		async (err) => {
			// if still fails on 401 lets retry
			if (
				ErrorHandlingService.extractStatusCode(err) === STATUS_CODES.UNAUTHORIZED &&
				ErrorHandlingService.extractErrorCode(err) === ERROR_CODES.TOKEN_INVALID
			) {
				const maybeChangedConfigWithNewToken = await refreshTokenMiddleware(err.config);
				return axios(maybeChangedConfigWithNewToken); // retry
			} else {
				try {
					tokenErrorHandler({ err });
				} catch {
					billingErrorHandler({ err });
				}
			}
		}
	);
	return api;
};

// Main API
export const api = createApi();
api.interceptors.response.use((res) => {
	if (res.config && '$fullData' in res.config && res.config.$fullData) {
		return res.data;
	}
	const { results } = Object(res.data);
	return results || res.data;
});

export const fullDataResponseApi = createApi();
export const fullResponseApi = createApi();
export const autoPaginatedApi = createApi();
export const runnerApi = createApi();

/**
 * This api is used for non authed requests, so we dont send JWT tokens to endpoints that dont need it.
 */
export const nonAuthedApi = axios.create({
	baseURL: VUE_APP_API,
	headers: cloneDeep(axios.defaults.headers),
});
nonAuthedApi.interceptors.response.use((res) => res.data);

autoPaginatedApi.interceptors.response.use((res) => {
	const { results, next } = Object(res.data);
	if (!results) return res.data;
	return next ? autoPaginatedApi.get(next).then((nextResults) => results.concat(nextResults)) : results;
});

fullDataResponseApi.interceptors.response.use((res) => res.data);

export const flowRunApi = createApi();
flowRunApi.interceptors.response.use(
	(res) => res.data, // fullDataResponseApi
	async (err) => {
		// if still fails on 401 its maybe because we are running with cookies in flow run
		if (
			ErrorHandlingService.extractStatusCode(err) === STATUS_CODES.UNAUTHORIZED &&
			ErrorHandlingService.extractErrorCode(err) === ERROR_CODES.TOKEN_INVALID
		) {
			await refreshForRunner('fake-refresh-using-cookie'); // refresh cookie token
			return axios(err.config); //retry
		} else {
			return Promise.reject(err);
		}
	}
);

// APIGW
const APIGW_AUTH_HEADAER = 'x-api-key';
export const apigw = axios.create({
	baseURL: VUE_APP_APIGW,
	headers: cloneDeep(axios.defaults.headers),
});
apigw.interceptors.response.use(({ data }) => data);

// Token callbacks
const mainApis = [api, verifyTokenApi, fullDataResponseApi, fullResponseApi, autoPaginatedApi, runnerApi, flowRunApi];

export const setToken = (token: string) => {
	if (!token) {
		return;
	}
	const tokenHeader = tokenStructure(token);
	mainApis.forEach((api) => (api.defaults.headers.common.Authorization = tokenHeader));
	apigw.defaults.headers.common[APIGW_AUTH_HEADAER] = tokenHeader;
};

export const removeToken = () => {
	mainApis.forEach((api) => delete api.defaults.headers.common.Authorization);
	delete apigw.defaults.headers.common[APIGW_AUTH_HEADAER];
};

export const buildCrud = <T, P, R>(client: AxiosInstance, entity: string) => ({
	/**
	 * T - type of id
	 * P - type of payload
	 * R - type of response
	 */
	list: () => client.get<{ results: R[] }>(`/${entity}/`),
	get: (id: T) => client.get<R>(`/${entity}/${id}/`),
	create: (payload: P) => client.post<R>(`/${entity}/`, payload),
	update: (id: T, payload: P) => client.put<R>(`/${entity}/${id}/`, payload),
	partialUpdate: (id: T, payload: Partial<P>) => client.patch<R>(`/${entity}/${id}/`, payload),
	delete: (id: T) => client.delete(`/${entity}/${id}/`),
});
